import type { LeaderboardConfig, LeaderboardMetric } from "@expert/app-config/types";
import { useFeatures, type WorkspaceFeatures } from "@expert/features";
import { Flex, Indicator, Text } from "@mantine/core";
import { type JSX, useState } from "react";
import { getMetricsDateRange } from "../utils/getMetricsDateRange";
import { Leaderboard } from "./Leaderboard";
import { LeaderboardControl } from "./LeaderboardControl";
import classes from "./LeaderboardWidget.module.css";

interface LeaderboardContent {
    controlText: string;
    description: string;
    reminder: string;
}

interface LeaderboardWidgetProps {
    leaderboards: LeaderboardConfig[];
}

export function LeaderboardWidget({ leaderboards }: LeaderboardWidgetProps): JSX.Element {
    const [activeMetric, setActiveMetric] = useState<LeaderboardMetric>(leaderboards[0]?.metric);
    const {
        features: { isSolveLeaderboardEnabled },
    } = useFeatures<WorkspaceFeatures>();
    const metricsDateRange = getMetricsDateRange();

    const leaderboardsByMetric = leaderboards.reduce(
        (acc, board) => {
            acc[board.metric] = {
                controlText: board.controlText,
                description: board.description.replace("$METRICS_DATE_RANGE", metricsDateRange),
                reminder: board.reminder,
            };
            return acc;
        },
        {} as Record<LeaderboardMetric, LeaderboardContent>,
    );

    const activeLeaderboard = leaderboardsByMetric[activeMetric];

    return (
        <Flex className={classes.main} data-testid="leaderboard-widget">
            <Flex direction="column" pt="xxs" pb="xs" px="sm">
                <Flex fw={700} fz="sm" mb="3xs" className={classes.header}>
                    Team leaderboard
                    <Flex pr="3xs" align="center">
                        <Indicator mr="3xs" size={6} processing color="primary.5" />
                        <Text ml="3xs" fz="xxs" c="dark.2">
                            Live
                        </Text>
                    </Flex>
                </Flex>
                {isSolveLeaderboardEnabled && leaderboards.length > 1 && (
                    <LeaderboardControl
                        leaderboards={leaderboards}
                        activeMetric={activeMetric}
                        onSetActiveMetric={setActiveMetric}
                    />
                )}
                <Flex c="white" fz="xs" pt="3xs">
                    {activeLeaderboard.description}
                </Flex>
                <Flex c="dark.2" fz="xs">
                    {activeLeaderboard.reminder}
                </Flex>
            </Flex>
            {leaderboards.map((board) => (
                <Leaderboard
                    key={board.metric}
                    isVisible={activeMetric === board.metric}
                    metric={board.metric}
                    sku={board.sku}
                />
            ))}
        </Flex>
    );
}
